import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Switch, Route, Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { getDimensions } from './store/actions/dimensions';
import { getToken } from './store/actions/token';
import { WardrobeHeightProvider } from './contexts/WardrobeHeightContext';
import { ActiveWardrobeProvider } from './contexts/ActiveWardrobeContext';
import { MoveWardrobeProvider } from './contexts/MoveWardrobeContext';
import { WardrobesConfigsProvider } from './contexts/WardrobesConfigsContext';
import { ConfiguratorModeProvider } from './contexts/ConfiguratorModeContext';
import { GAProvider } from 'contexts/GAContext';
import { EventsDataProvider } from './contexts/EventsDataContext';
import StartScreen from './views/StartScreen';
import HeightSettings from './views/HeightSettings';
import WardrobeTypeSelection from './views/WardrobeTypeSelection';
import WardrobeBodyConfiguration from './views/WardrobeBodyConfiguration';
import WardrobeFrontsConfiguration from './views/WardrobeFrontsConfiguration';
import WardrobeHandlesConfiguration from './views/WardrobeHandlesConfiguration';
import SkipAccessories from './views/SkipAccessories';
import BuyAccessories from './views/BuyAccessories';
import BuyWardrobeBody from './views/BuyWardrobeBody';
import AccessoriesConfiguration from './views/AccessoriesConfiguration';
import WorkInProgress from './views/WorkInProgress';
import SummaryScreen from './views/SummaryScreen';
import Basket from './views/Basket';
import NotFound from './views/NotFound';
import './App.scss';
import SetupComponent from './components/common/SetupComponent';
import ReactGA from 'react-ga';
import Analytics from './services/Analytics';
import { PriceProvider } from './contexts/PriceContext';
import {
  getCornerHulls,
  getHandles,
  getWardrobeHulls,
  getImagesByType,
} from './store/actions/products';
import { Redirect } from 'react-router-dom';
import { reactLocalStorage } from 'reactjs-localstorage';

const history = createBrowserHistory({
  basename: process.env.REACT_APP_BASENAME_URL,
});

history.listen(location => {
  ReactGA.set({ page: location.pathname }, ['tracker1', 'tracker2']);
  ReactGA.pageview(location.pathname, ['tracker1', 'tracker2']);
});

ReactGA.initialize(
  [
    {
      trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS_TRACK_ID,
      gaOptions: {
        name: 'tracker1',
      },
    },
    {
      trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS_TRACK_ID_2,
      gaOptions: { name: 'tracker2' },
    },
  ],
  { debug: false, alwaysSendToDefaultTracker: false },
);

const App = props => {
  const { imagesList, getImagesByType } = props;

  const loadImage = (path, type, alias) => {
    const newImage = new Image();
    newImage.src = path;
    window[type][alias] = newImage;
  };

  useEffect(() => {
    props.getDimensions();
    props.getHandles();
    props.getWardrobeHulls();
    props.getCornerHulls();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (Object.values(imagesList.textures).length === 0) {
      getImagesByType();
    } else {
      Object.keys(imagesList).forEach(type => {
        window[type] = {};
        switch (type) {
          case 'textures':
            Object.keys(imagesList[type]).forEach(key => {
              const image = imagesList[type][key];
              loadImage(image.path, type, key);
            });
            break;

          case 'icons':
          case 'models_3d':
            Object.keys(imagesList[type]).forEach(key => {
              const image = imagesList[type][key];
              loadImage(image, type, key);
            });
            break;

          default:
            break;
        }
      });
    }
    /* eslint-disable-next-line */
  }, [imagesList]);

  let wardrobe = reactLocalStorage.getObject('wardrobesConfigs');

  return (
    <Router basename={process.env.REACT_APP_BASENAME_URL} history={history}>
      <ConfiguratorModeProvider>
        <WardrobeHeightProvider>
          <ActiveWardrobeProvider>
            <WardrobesConfigsProvider>
              <PriceProvider>
                <MoveWardrobeProvider>
                  <EventsDataProvider>
                    <GAProvider>
                      <div className="App">
                        <SetupComponent dimensions={props.dimensions}>
                          <Switch>
                            <Route exact path="/" component={StartScreen} />
                            <Route exact path="/work-in-progress" component={WorkInProgress} />
                            <Route
                              path="/buy-accessories"
                              render={() => <BuyAccessories {...props} />}
                            />
                            <Route
                              path="/buy-wardrobe-body"
                              render={() => <BuyWardrobeBody {...props} />}
                            />
                            <Route
                              path="/height-settings"
                              render={() => <HeightSettings {...props} />}
                            />
                            <Route
                              path="/wardrobe-type"
                              render={() => <WardrobeTypeSelection {...props} />}
                            />
                            <Route
                              path="/wardrobe-body-configuration"
                              render={() => <WardrobeBodyConfiguration {...props} />}
                            />
                            <Route
                              path="/wardrobe-fronts-configuration"
                              component={WardrobeFrontsConfiguration}
                            />
                            <Route
                              path="/wardrobe-handles-configuration"
                              component={WardrobeHandlesConfiguration}>
                              {wardrobe.length === 0 ? <Redirect to="/" /> : ''}
                            </Route>
                            <Route path="/accessories-skip" component={SkipAccessories}>
                              {wardrobe.length === 0 ? <Redirect to="/" /> : ''}
                            </Route>
                            <Route
                              path="/accessories-configuration"
                              component={AccessoriesConfiguration}>
                              {wardrobe.length === 0 ? <Redirect to="/" /> : ''}
                            </Route>
                            <Route path="/summary/:shortToken?">
                              <SummaryScreen {...props} />
                            </Route>
                            <Route path="/basket" component={Basket} />
                            <Route component={NotFound} />
                          </Switch>
                        </SetupComponent>
                      </div>
                    </GAProvider>
                  </EventsDataProvider>
                </MoveWardrobeProvider>
              </PriceProvider>
            </WardrobesConfigsProvider>
          </ActiveWardrobeProvider>
        </WardrobeHeightProvider>
        <Analytics />
      </ConfiguratorModeProvider>
    </Router>
  );
};

const mapStateToProps = state => {
  return {
    ...state,
    dimensions: state.dimensions.dimensions,
    token: state.token.token,
    imagesList: state.products.imagesList,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getToken: () => dispatch(getToken()),
    getHandles: () => dispatch(getHandles()),
    getDimensions: () => dispatch(getDimensions()),
    getWardrobeHulls: () => dispatch(getWardrobeHulls()),
    getCornerHulls: () => dispatch(getCornerHulls()),
    getImagesByType: () => dispatch(getImagesByType()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
